import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useModelScenario } from "@services/api";
import { ModelScenario } from "@services/api/types";
import { toLocaleString } from "@utils/numbers";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

const toTableRow = (scenario: ModelScenario): JSX.Element => {
  return (
    <StyledTableRow key={scenario.scenario.id}>
      <StyledTableCell align="center">{scenario.scenario.name}</StyledTableCell>
      <StyledTableCell align="center">
        {toLocaleString(scenario.scenario.media_investment, 0)}
      </StyledTableCell>
      <StyledTableCell align="center">
        {toLocaleString(scenario.scenario.predicted_inflow, 0)}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export interface ScenarioMediaKpiTableProps {
  scenario1Id: string;
  scenario2Id: string;
}

const ScenarioMediaKpiTable = ({
  scenario1Id,
  scenario2Id
}: ScenarioMediaKpiTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { currentClient } = useContext(ClientContext);

  const { data: scenario1, isFetching: isLoadingScenario1 } = useModelScenario(
    scenario1Id ?? ""
  );
  const { data: scenario2, isFetching: isLoadingScenario2 } = useModelScenario(
    scenario2Id ?? ""
  );

  const isLoading = useMemo(() => {
    return isLoadingScenario1 || isLoadingScenario2;
  }, [isLoadingScenario1, isLoadingScenario2]);

  const tableData = useMemo(() => {
    const scenarios = [scenario1, scenario2];

    return scenarios
      .filter((scenario) => scenario !== undefined)
      .map((scenarioData) => {
        return toTableRow(scenarioData!!);
      });
  }, [scenario1, scenario2]);

  return (
    <Box className="w-full pt-2">
      <Box className={`w-full flex pt-4 ${isMobile ? "" : "hidden"}`}>
        <SwipeIcon
          sx={{
            bottom: 12,
            opacity: 0.4,
            left: "50%",
            transform: "translate(750%, 0%)",
            color: theme.palette.primary.main
          }}
          fontSize="small"
        />
      </Box>
      <TableContainer
        className="pt-1"
        sx={{
          overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
          scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
          "&::-webkit-scrollbar": {
            display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
          }
        }}
      >
        <Table className="w-full" aria-label="benchmark table">
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">
                {t("simulator.scenarios.table.scenario")}
              </StyledTableCell>
              <StyledTableCell align="center">
                {t("simulator.scenarios.table.media_spend", {
                  currency: currentClient.client.currency
                })}
              </StyledTableCell>
              <StyledTableCell align="center">
                {t("simulator.scenarios.table.inflow", {
                  kpi: currentClient.client.kpi_label
                })}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRowsSkeleton
                id={`${currentClient.model.id}-benchmark-scenario-media-kpi-table-skeleton`}
                numberOfRows={2}
                columns={3}
              />
            ) : (
              tableData
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ScenarioMediaKpiTable;
